import { db, UserStats as DBUserStats } from '../utils/db';

/**
 * バッジシステム管理サービス (IndexedDB 統合版)
 * サーバー依存を完全に排除し、ローカルデータを唯一の真実とします。
 */

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'streak' | 'milestone' | 'special' | 'weekly' | 'monthly';
  requirement: number;
  requirementType: 'streak' | 'total_completed' | 'consecutive_days' | 'habits_count' | 'perfect_week';
  unlockedAt?: string;
  progress?: number;
  isUnlocked: boolean;
}

export type UserStats = DBUserStats;

const BADGE_DEFINITIONS: Omit<Badge, 'unlockedAt' | 'isUnlocked' | 'progress'>[] = [
  { id: 'streak_3', name: '🔥 3日連続', description: '3日連続で習慣を達成', icon: '🔥', category: 'streak', requirement: 3, requirementType: 'consecutive_days' },
  { id: 'streak_7', name: '⚡ 週間マスター', description: '7日連続で習慣を達成', icon: '⚡', category: 'streak', requirement: 7, requirementType: 'consecutive_days' },
  { id: 'streak_30', name: '🌟 月間チャンピオン', description: '30日連続で習慣を達成', icon: '🌟', category: 'streak', requirement: 30, requirementType: 'consecutive_days' },
  { id: 'streak_100', name: '💎 100日の偉業', description: '100日連続で習慣を達成', icon: '💎', category: 'streak', requirement: 100, requirementType: 'consecutive_days' },
  { id: 'streak_365', name: '👑 年間王者', description: '365日連続で習慣を達成', icon: '👑', category: 'streak', requirement: 365, requirementType: 'consecutive_days' },
  { id: 'total_10', name: '🌱 はじめの一歩', description: '合計10回達成', icon: '🌱', category: 'milestone', requirement: 10, requirementType: 'total_completed' },
  { id: 'total_50', name: '🌿 成長中', description: '合計50回達成', icon: '🌿', category: 'milestone', requirement: 50, requirementType: 'total_completed' },
  { id: 'total_100', name: '🌳 習慣の木', description: '合計100回達成', icon: '🌳', category: 'milestone', requirement: 100, requirementType: 'total_completed' },
  { id: 'total_500', name: '🏆 500回の達成', description: '合計500回達成', icon: '🏆', category: 'milestone', requirement: 500, requirementType: 'total_completed' },
  { id: 'total_1000', name: '🎯 1000回マスター', description: '合計1000回達成', icon: '🎯', category: 'milestone', requirement: 1000, requirementType: 'total_completed' },
  { id: 'perfect_week_1', name: '📅 完璧な週', description: '1週間すべての習慣を達成', icon: '📅', category: 'weekly', requirement: 1, requirementType: 'perfect_week' },
  { id: 'perfect_week_4', name: '🗓️ 月間パーフェクト', description: '4週間連続で完璧な週を達成', icon: '🗓️', category: 'weekly', requirement: 4, requirementType: 'perfect_week' },
  { id: 'habits_3', name: '📝 マルチタスカー', description: '3つの習慣を管理', icon: '📝', category: 'special', requirement: 3, requirementType: 'habits_count' },
  { id: 'habits_5', name: '🎪 習慣ジャグラー', description: '5つの習慣を管理', icon: '🎪', category: 'special', requirement: 5, requirementType: 'habits_count' },
  { id: 'habits_10', name: '🎭 習慣マエストロ', description: '10個の習慣を管理', icon: '🎭', category: 'special', requirement: 10, requirementType: 'habits_count' },
  { id: 'early_bird', name: '🌅 早起き鳥', description: '朝4時〜8時に習慣を達成（30回）', icon: '🌅', category: 'special', requirement: 30, requirementType: 'total_completed' },
  { id: 'night_owl', name: '🦉 夜型マスター', description: '夜22時〜朝4時に習慣を達成（30回）', icon: '🦉', category: 'special', requirement: 30, requirementType: 'total_completed' },
  { id: 'weekend_warrior', name: '🎉 週末戦士', description: '週末50日分の習慣を達成', icon: '🎉', category: 'special', requirement: 50, requirementType: 'total_completed' }
];

class BadgeService {
  private badges: Map<string, Badge> = new Map();
  private stats: UserStats | null = null;
  private isInitialized: boolean = false;

  public async initialize(): Promise<void> {
    try {
      if (this.isInitialized) return;
      
      // 統計をロード (initDB で確実に生成されているはず)
      let stats = await db.stats.get('current');
      if (!stats) {
        throw new Error("Critical: DB not initialized properly. Missing 'current' stats.");
      }
      this.stats = stats;

      // バッジをロード
      const states = await db.badges.toArray();
      const stateMap = new Map(states.map(s => [s.id, s]));

      this.badges.clear(); // 初期化時に一旦クリア
      BADGE_DEFINITIONS.forEach(def => {
        const state = stateMap.get(def.id);
        this.badges.set(def.id, {
          ...def,
          isUnlocked: state?.isUnlocked || false,
          unlockedAt: state?.unlockedAt ? (state.unlockedAt instanceof Date ? state.unlockedAt.toISOString() : String(state.unlockedAt)) : undefined,
          progress: state?.progress || 0
        });
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('[BadgeService] Initialization failed:', error);
      // 再初期化を試みれるように isInitialized は false のまま、エラーを再投
      throw error;
    }
  }

  // 統計を更新（IndexedDB 同期）
  public async updateStats(updates: Partial<UserStats>): Promise<void> {
    if (!this.stats) await this.initialize();
    this.stats = { ...this.stats!, ...updates };
    await db.stats.put(this.stats);
  }

  // バッジ解除チェック（IndexedDB 同期）
  public async checkBadgeProgress(stats?: UserStats): Promise<Badge[]> {
    if (!this.isInitialized) await this.initialize();
    const currentStats = stats || this.stats || this.getStats();
    const newlyUnlocked: Badge[] = [];
    
    for (const badge of Array.from(this.badges.values())) {
      if (!badge.isUnlocked) {
        let progress = 0;
        let shouldUnlock = false;
        
        switch (badge.id) {
          case 'early_bird': progress = currentStats.earlyBirdCount || 0; break;
          case 'night_owl': progress = currentStats.nightOwlCount || 0; break;
          case 'weekend_warrior': progress = currentStats.weekendCount || 0; break;
          default:
            switch (badge.requirementType) {
              case 'consecutive_days': progress = currentStats.currentStreak; break;
              case 'total_completed': progress = currentStats.totalCompleted; break;
              case 'perfect_week': progress = currentStats.perfectWeeks; break;
              case 'habits_count': progress = currentStats.habitsCount; break;
            }
        }
        
        badge.progress = Math.min(progress, badge.requirement);
        shouldUnlock = progress >= badge.requirement;
        
        if (shouldUnlock) {
          badge.isUnlocked = true;
          badge.unlockedAt = new Date().toISOString();
          newlyUnlocked.push(badge);
          
          // IndexedDB に保存
          await db.badges.put({
            id: badge.id,
            isUnlocked: true,
            unlockedAt: new Date(badge.unlockedAt),
            progress: badge.progress
          });
        }
      }
    }
    
    return newlyUnlocked;
  }

  public getStats(): UserStats {
    return this.stats || {
      id: 'current',
      userId: 'local',
      totalCompleted: 0,
      currentStreak: 0,
      longestStreak: 0,
      perfectWeeks: 0,
      perfectMonths: 0,
      habitsCount: 0,
      totalDays: 0,
      joinedDate: new Date(),
      earlyBirdCount: 0,
      nightOwlCount: 0,
      weekendCount: 0
    };
  }

  public getAllBadges(): Badge[] {
    return Array.from(this.badges.values());
  }

  public getCompletionRate(): number {
    const total = this.badges.size;
    const unlocked = Array.from(this.badges.values()).filter(b => b.isUnlocked).length;
    return total > 0 ? (unlocked / total) * 100 : 0;
  }
}

export const badgeService = new BadgeService();
